import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { PanelLeftOpen as ExpandIcon, Search as SearchIcon } from "lucide-react";
import type { ConversationListItemDto, ConversationListSortName } from "@multi-chat/shared";
import { SimpleTooltip } from "../../../components/ui/tooltip/index.js";
import { cn } from "../../../lib/utils.js";
import { ConversationAvatar } from "../ConversationAvatar/ConversationAvatar.js";
import { useConversationDraftSnapshot } from "../conversation-draft-context.js";
import { orderConversationsByEffectiveActivity } from "../conversation-draft-presentation.js";
import { conversationRowUnreadLabel } from "../conversation-list-row.js";
import { conversationTitle } from "../inbox-display.js";
import { InboxIconButton } from "../InboxIconButton.js";

/** 64px collapsed list: expand, search, and one avatar per loaded conversation. */
export function ConversationListStrip({
  items,
  selectedConversationId,
  sort,
  searchActive,
  onSelect,
  onExpand,
  onSearch,
  expandButtonRef,
}: {
  items: ConversationListItemDto[];
  selectedConversationId: number | null;
  sort: ConversationListSortName;
  searchActive: boolean;
  onSelect: (conversationId: number) => void;
  onExpand: () => void;
  onSearch: () => void;
  expandButtonRef?: RefObject<HTMLButtonElement | null>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const drafts = useConversationDraftSnapshot();
  const presentedItems = orderConversationsByEffectiveActivity(items, drafts, sort);

  return (
    <div className="flex h-full w-[62px] flex-col items-center gap-1 pt-3 pb-2">
      <SimpleTooltip content={t("inbox.list.expand")}>
        <InboxIconButton
          ref={expandButtonRef}
          aria-label={t("inbox.list.expand")}
          aria-expanded={false}
          onClick={onExpand}
        >
          <ExpandIcon size={18} className="rtl:-scale-x-100" aria-hidden="true" />
        </InboxIconButton>
      </SimpleTooltip>
      <SimpleTooltip content={t("inbox.search.label")}>
        <InboxIconButton aria-label={t("inbox.search.label")} active={searchActive} onClick={onSearch}>
          <SearchIcon size={18} aria-hidden="true" />
          {searchActive ? (
            <span
              className="absolute top-1.5 end-1.5 size-[7px] rounded-full bg-primary shadow-[0_0_0_2px_#fff]"
              aria-hidden="true"
            />
          ) : null}
        </InboxIconButton>
      </SimpleTooltip>
      <span className="my-1 h-px w-7 bg-inbox-line" aria-hidden="true" />
      <ul
        className="m-0 flex min-h-0 w-full flex-1 list-none flex-col items-center gap-2 overflow-y-auto px-0 pt-1.5 pb-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label={t("inbox.listLabel")}
      >
        {presentedItems.map((item) => {
          const title = conversationTitle(item, t);
          const unreadLabel = conversationRowUnreadLabel(item.unreadCount);
          const selected = item.conversationId === selectedConversationId;
          const label =
            unreadLabel == null
              ? title
              : `${title}, ${t("inbox.unreadCount", { count: item.unreadCount })}`;
          return (
            <li key={item.conversationId}>
              <SimpleTooltip content={title}>
                <button
                  type="button"
                  aria-label={label}
                  aria-current={selected || undefined}
                  className={cn(
                    "relative rounded-full p-0.5 transition-[transform,box-shadow] duration-200 ease-inbox hover:-translate-y-px motion-reduce:transition-none",
                    "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
                    selected && "shadow-[0_0_0_2px_#fff,0_0_0_4px_var(--color-primary)]",
                  )}
                  onClick={() => onSelect(item.conversationId)}
                >
                  <ConversationAvatar
                    title={title}
                    imageUrl={item.contactPlatformProfile.platformProfileImageUrl}
                    platformType={item.platformType}
                    toneSeed={item.contactPlatformProfile.id}
                    size="sm"
                  />
                  {unreadLabel ? (
                    <span
                      className="absolute -top-0.5 -end-[3px] grid h-[17px] min-w-[17px] place-items-center rounded-full bg-primary px-1 text-[0.59rem] leading-none font-bold text-white tabular-nums shadow-[0_0_0_2px_#fff]"
                      aria-hidden="true"
                    >
                      {unreadLabel}
                    </span>
                  ) : null}
                </button>
              </SimpleTooltip>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
